/**
 * Правка текстов приложения с сервера — без новой сборки.
 *
 * ⚠️ ЗАЧЕМ. Тексты лежат ВНУТРИ сборки: правка на сайте до приложения не
 * доходит, нужна новая сборка и новая проверка в App Store — неделя ожидания.
 * 01.09.2026 ровно так и вышло: предложение с лендинга убрали, на сайте его
 * нет, а в приложении осталось.
 *
 * Устройство: сервер отдаёт правки по ключам перевода (`landing.freeText`),
 * приложение накладывает их ПОВЕРХ встроенных. Ключ не прислали — работает
 * встроенный текст. То есть сервер может поправить любую строку, но не может
 * оставить приложение без текста вовсе.
 */
export type TextOverrides = Record<string, Record<string, string>>;

/**
 * Оставляет только те правки, у которых есть перевод на нужный язык.
 *
 * ⚠️ ЧУЖОЙ ЯЗЫК НЕ ПОДСТАВЛЯЕМ. Казахоязычный ученик, увидевший русскую строку
 * посреди казахского экрана, получает впечатление поломки — это хуже, чем
 * прежний встроенный текст.
 *
 * Пустая строка — осмысленная правка: «убрать этот текст». Ровно наш случай.
 */
export function pickForLanguage(texts: TextOverrides | undefined, lang: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, byLang] of Object.entries(texts ?? {})) {
    if (!byLang || typeof byLang !== 'object') continue;
    const value = (byLang as Record<string, unknown>)[lang];
    if (typeof value === 'string') out[key] = value;
  }
  return out;
}

/**
 * Разворачивает плоские ключи в дерево: `landing.freeText` → `{landing: {freeText}}`.
 * Переводы хранятся деревом, и плоский ключ поверх него не встанет.
 */
export function flattenOverrides(flat: Record<string, string>): Record<string, unknown> {
  const tree: Record<string, unknown> = {};
  for (const [path, value] of Object.entries(flat)) {
    const parts = path.split('.');
    let node = tree;
    parts.forEach((part, i) => {
      if (i === parts.length - 1) {
        node[part] = value;
        return;
      }
      if (typeof node[part] !== 'object' || node[part] === null) node[part] = {};
      node = node[part] as Record<string, unknown>;
    });
  }
  return tree;
}
